import { Input } from "@ember/component";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import CookText from "discourse/components/cook-text";
import dIcon from "discourse/ui-kit/helpers/d-icon";

export default <template>
  <div class="discourse-events-calendar discourse-events-calendar--detail">
    <nav class="discourse-events-calendar__section-tabs" aria-label="Calendar sections">
      <a href="/calendar" class="discourse-events-calendar__section-tab">
        Calendar
      </a>
      <a href="/calendar/rooms" class="discourse-events-calendar__section-tab">
        Rooms
      </a>
      <a
        href="/calendar/locations"
        class="discourse-events-calendar__section-tab discourse-events-calendar__section-tab--active"
        aria-current="page"
      >
        Venues
      </a>
    </nav>

    {{#if @controller.hasLocation}}
      <section class="discourse-events-calendar__panel discourse-events-calendar__resource-detail">
        <header class="discourse-events-calendar__event-detail-header">
          <div>
            <h1>{{@controller.location.name}}</h1>
            <span class="discourse-events-calendar__event-status">
              {{@controller.publicationLabel}}
            </span>
          </div>

          <div class="discourse-events-calendar__detail-actions">
            <a
              href={{@controller.feedUrl}}
              class="btn discourse-events-calendar__icon-button"
              aria-label="Subscribe to venue calendar"
              title="Subscribe"
              data-auto-route="false"
              download="location-events.ics"
            >
              {{dIcon "calendar-days"}}
              <span class="sr-only">Subscribe</span>
            </a>

            <a
              href={{@controller.createEventUrl}}
              class="btn btn-primary discourse-events-calendar__icon-label-button"
            >
              {{dIcon "plus"}}
              <span>Create event</span>
            </a>

            {{#if @controller.canManageLocation}}
              <button
                type="button"
                class="btn discourse-events-calendar__icon-button"
                aria-label="Edit venue"
                title="Edit venue"
                {{on "click" @controller.startEditing}}
              >
                {{dIcon "pencil-alt"}}
                <span class="sr-only">Edit venue</span>
              </button>
            {{/if}}
          </div>
        </header>

        {{#if @controller.editNotice}}
          <div class="discourse-events-calendar__notice" role="status">
            {{@controller.editNotice}}
          </div>
        {{/if}}

        {{#if @controller.editError}}
          <div class="discourse-events-calendar__error" role="alert">
            {{@controller.editError}}
          </div>
        {{/if}}

        {{#if @controller.isDraft}}
          <div class="discourse-events-calendar__draft-note" role="status">
            <strong>Draft venue.</strong>
            It is visible to venue managers, but ordinary users cannot browse or reserve rooms from it.
          </div>
        {{/if}}

        {{#if @controller.isEditing}}
          <form
            class="discourse-events-calendar__edit-form discourse-events-calendar__resource-edit-form"
            {{on "submit" @controller.saveLocation}}
          >
            <div class="discourse-events-calendar__field-row">
              <div class="discourse-events-calendar__field">
                <label for="location-edit-name">Venue name</label>
                <Input
                  id="location-edit-name"
                  @value={{@controller.editName}}
                  required={{true}}
                />
              </div>

              <div class="discourse-events-calendar__field">
                <label for="location-edit-website">Website</label>
                <Input
                  id="location-edit-website"
                  @value={{@controller.editWebsite}}
                  placeholder="https://..."
                />
              </div>
            </div>

	            <div class="discourse-events-calendar__field">
                <label for="location-edit-address">Public address</label>
	              <Input
	                id="location-edit-address"
	                @value={{@controller.editAddress}}
	                placeholder="Street address, building, or campus area"
	              />
	            </div>

	            {{#if @controller.placesEnabled}}
	              <div class="discourse-events-calendar__field discourse-events-calendar__place-field">
	                <label for="location-edit-place-query">Linked place</label>
	                <div class="discourse-events-calendar__place-picker">
	                  <Input
	                    id="location-edit-place-query"
	                    @value={{@controller.editPlaceQuery}}
	                    placeholder="Search Places"
	                    autocomplete="off"
	                    {{on "input" @controller.updateEditPlaceQuery}}
	                  />
	                  {{#if @controller.hasEditPlaceOptions}}
	                    <div class="discourse-events-calendar__place-options" role="listbox" aria-label="Matching places">
	                      {{#each @controller.editPlaceOptions as |place|}}
	                        <button
	                          type="button"
	                          class="btn-flat discourse-events-calendar__place-option"
	                          role="option"
	                          {{on "click" (fn @controller.selectEditPlace place)}}
	                        >
	                          <strong>{{place.name}}</strong>
	                          {{#if place.address}}
	                            <span>{{place.address}}</span>
	                          {{/if}}
	                        </button>
	                      {{/each}}
	                    </div>
	                  {{/if}}
	                </div>
	                {{#if @controller.isLoadingEditPlaces}}
	                  <p class="discourse-events-calendar__field-hint">Searching places...</p>
	                {{/if}}
	                {{#if @controller.editPlaceId}}
	                  <button type="button" class="btn btn-small" {{on "click" @controller.clearEditPlace}}>
	                    Clear place
	                  </button>
	                {{/if}}
	                <p class="discourse-events-calendar__field-hint">
	                  Optional; Places owns precise maps and geocoding.
	                </p>
	              </div>
	            {{/if}}

            <div class="discourse-events-calendar__field">
              <label for="location-edit-image-upload">Venue image</label>
              <input
                id="location-edit-image-upload"
                type="file"
                accept="image/*"
                disabled={{@controller.isUploadingEditImage}}
                {{on "change" @controller.uploadEditImage}}
              />
              {{#if @controller.hasEditImageUpload}}
                <div class="discourse-events-calendar__image-upload-preview">
                  <img src={{@controller.editImagePreviewUrl}} alt="" />
                  <button type="button" class="btn btn-small" {{on "click" @controller.removeEditImage}}>
                    Remove image
                  </button>
                </div>
              {{/if}}
              {{#if @controller.isUploadingEditImage}}
                <p class="discourse-events-calendar__field-hint">Uploading image...</p>
              {{/if}}
              {{#if @controller.editImageUploadError}}
                <p class="discourse-events-calendar__form-error">{{@controller.editImageUploadError}}</p>
              {{/if}}
            </div>

	            <div class="discourse-events-calendar__field-row">
              <div class="discourse-events-calendar__field">
                <label for="location-edit-publication-status">Publication</label>
                <select
                  id="location-edit-publication-status"
                  value={{@controller.editPublicationStatus}}
                  {{on "change" @controller.updatePublicationStatus}}
                >
                  <option value="published" selected={{@controller.isPublishedPublicationStatus}}>Published</option>
                  <option value="draft" selected={{@controller.isDraftPublicationStatus}}>Draft</option>
                </select>
              </div>

              <div class="discourse-events-calendar__field">
                <label for="location-edit-hours-timezone">Hours timezone</label>
                <select
                  id="location-edit-hours-timezone"
                  value={{@controller.editHoursTimezone}}
                  {{on "change" @controller.updateHoursTimezone}}
                >
                  {{#each @controller.editHoursTimezoneOptions as |timezone|}}
                    <option value={{timezone.value}} selected={{timezone.isSelected}}>{{timezone.label}}</option>
                  {{/each}}
                </select>
              </div>
            </div>

            <fieldset class="discourse-events-calendar__hours-editor">
              <legend>Hours of operation</legend>
              <p class="discourse-events-calendar__field-hint">
                Rooms at this venue cannot be reserved outside these checked days and times.
              </p>

              {{#each @controller.editHoursFields as |day|}}
                <div class="discourse-events-calendar__hours-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={{day.enabled}}
                      {{on "change" (fn @controller.toggleHoursDay day.key)}}
                    />
                    <span>{{day.label}}</span>
                  </label>
                  <Input
                    @type="time"
                    @value={{day.start}}
                    step="900"
                    {{on "input" (fn @controller.updateHoursStart day.key)}}
                  />
                  <Input
                    @type="time"
                    @value={{day.end}}
                    step="900"
                    {{on "input" (fn @controller.updateHoursEnd day.key)}}
                  />
                </div>
              {{/each}}
            </fieldset>

            <div class="discourse-events-calendar__field">
              <label for="location-edit-description">Description</label>
              <textarea
                id="location-edit-description"
                value={{@controller.editDescription}}
                rows="6"
                placeholder="Access notes, floor, entrance, parking, or other attendee details"
                {{on "input" @controller.updateDescription}}
              ></textarea>
            </div>

            <div class="discourse-events-calendar__inline-actions">
              <button
                type="submit"
                class="btn btn-primary"
                disabled={{@controller.cannotSaveLocation}}
              >
                {{#if @controller.isSavingLocation}}
                  Saving...
                {{else}}
                  Save venue
                {{/if}}
              </button>
              <button type="button" class="btn" {{on "click" @controller.cancelEditing}}>
                Cancel
              </button>
            </div>
          </form>
        {{else}}
          <div class="discourse-events-calendar__venue-layout">
            <div class="discourse-events-calendar__venue-main">
              {{#if @controller.hasLocationImage}}
                <div class="discourse-events-calendar__resource-hero discourse-events-calendar__resource-hero--venue">
                  <img
                    src={{@controller.location.image_url}}
                    alt={{@controller.locationImageAlt}}
                    loading="lazy"
                  />
                </div>
              {{/if}}

              {{#if @controller.hasPlace}}
                <section class="discourse-events-calendar__venue-summary">
                  {{#if @controller.hasPlaceThumbnail}}
                    <a href={{@controller.placeUrl}} class="discourse-events-calendar__venue-summary-thumb">
                      <img src={{@controller.placeThumbnailUrl}} alt="" loading="lazy" />
                    </a>
                  {{/if}}

                  <div class="discourse-events-calendar__venue-summary-body">
                    <span class="discourse-events-calendar__compact-kicker">Linked place</span>
                    <h2>
                      {{#if @controller.hasPlaceUrl}}
                        <a href={{@controller.placeUrl}}>
                          {{@controller.location.place.name}}
                        </a>
                      {{else}}
                        {{@controller.location.place.name}}
                      {{/if}}
                    </h2>

                    <dl class="discourse-events-calendar__compact-meta">
                      {{#if @controller.hasPlaceTypeLabel}}
                        <div>
                          <dt>Type</dt>
                          <dd>{{@controller.placeTypeLabel}}</dd>
                        </div>
                      {{/if}}

                      {{#if @controller.placeAddress}}
                        <div>
                          <dt>Address</dt>
                          <dd>{{@controller.placeAddress}}</dd>
                        </div>
                      {{/if}}

                      {{#if @controller.hasHours}}
                        <div>
                          <dt>Hours</dt>
                          <dd>{{@controller.location.hours_summary}}</dd>
                        </div>
                      {{/if}}

                      {{#if @controller.hasPlaceWebsite}}
                        <div>
                          <dt>Website</dt>
                          <dd>
                            <a href={{@controller.placeWebsite}} target="_blank" rel="noopener noreferrer">
                              Visit website
                            </a>
                          </dd>
                        </div>
                      {{else if @controller.hasWebsite}}
                        <div>
                          <dt>Website</dt>
                          <dd>
                            <a href={{@controller.websiteUrl}} target="_blank" rel="noopener noreferrer">
                              Visit website
                            </a>
                          </dd>
                        </div>
                      {{/if}}
                    </dl>
                  </div>
                </section>
              {{else}}
                <dl class="discourse-events-calendar__event-facts discourse-events-calendar__event-facts--venue">
                  {{#if @controller.hasStandaloneAddress}}
                    <div>
                      <dt>Address</dt>
                      <dd>{{@controller.location.address}}</dd>
                    </div>
                  {{/if}}

                  {{#if @controller.hasWebsite}}
                    <div>
                      <dt>Website</dt>
                      <dd>
                        <a href={{@controller.websiteUrl}} target="_blank" rel="noopener noreferrer">
                          Visit website
                        </a>
                      </dd>
                    </div>
                  {{/if}}

                  {{#if @controller.hasHours}}
                    <div>
                      <dt>Hours</dt>
                      <dd>{{@controller.location.hours_summary}}</dd>
                    </div>
                  {{/if}}
                </dl>
              {{/if}}

              {{#if @controller.hasDescription}}
                <div class="discourse-events-calendar__event-detail-description discourse-events-calendar__venue-description">
                  <h2>Details</h2>
                  <CookText
                    @rawText={{@controller.location.description}}
                    class="cooked"
                  />
                </div>
              {{/if}}

              <section class="discourse-events-calendar__compact-section discourse-events-calendar__venue-events-section">
                <div class="discourse-events-calendar__panel-header">
                  <div>
                    <h2>Upcoming events</h2>
                    <p>Events currently scheduled at this venue.</p>
                  </div>
                </div>

                {{#if @controller.hasUpcomingEvents}}
                  <ol class="discourse-events-calendar__events discourse-events-calendar__events--compact">
                    {{#each @controller.upcomingEvents as |event|}}
                      <li class="discourse-events-calendar__event">
                        <div class="discourse-events-calendar__event-time">
                          <span class="discourse-events-calendar__event-date">{{event.displayDate}}</span>
                          {{#if event.displayTimeRange}}
                            <span>{{event.displayTimeRange}}</span>
                          {{/if}}
                        </div>
                        <div class="discourse-events-calendar__event-body">
                          <h3>
                            <a href={{event.detailUrl}}>
                              {{event.title}}
                            </a>
                          </h3>

                          {{#if event.room}}
                            <p class="discourse-events-calendar__event-room">
                              <a href={{event.room.detailUrl}}>
                                {{event.room.name}}
                              </a>
                            </p>
                          {{/if}}
                        </div>
                      </li>
                    {{/each}}
                  </ol>
                {{else}}
                  <p class="discourse-events-calendar__empty">
                    No upcoming events are scheduled at this venue.
                  </p>
                {{/if}}
              </section>
            </div>

            <aside class="discourse-events-calendar__venue-aside">
              {{#if @controller.hasPlaceMap}}
                <section class="discourse-events-calendar__compact-section">
                  <div class="discourse-events-calendar__compact-section-header">
                    <h2>Map</h2>
                  </div>
                  <div
                    class="discourse-events-calendar__map-panel discourse-events-calendar__map-panel--place discourse-events-calendar__map-panel--compact"
                    aria-label="Map for {{@controller.location.place.name}}"
                    {{didInsert @controller.renderPlaceMap}}
                    {{willDestroy @controller.destroyPlaceMap}}
                  ></div>
                </section>
              {{/if}}

              <section class="discourse-events-calendar__compact-section">
                <div class="discourse-events-calendar__panel-header">
                  <div>
                    <h2>Rooms</h2>
                    <p>Reservable rooms at this venue.</p>
                  </div>
                </div>

                {{#if @controller.hasRooms}}
                  <ol class="discourse-events-calendar__resource-list discourse-events-calendar__resource-list--compact">
                    {{#each @controller.rooms as |room|}}
                      <li class="discourse-events-calendar__resource-list-item">
                        {{#if room.image_url}}
                          <a href={{room.detailUrl}} class="discourse-events-calendar__resource-thumb">
                            <img src={{room.image_url}} alt="" loading="lazy" />
                          </a>
                        {{/if}}

                        <div>
                          <h3>
                            <a href={{room.detailUrl}}>
                              {{room.name}}
                            </a>
                          </h3>

                          {{#if room.description}}
                            <p>{{room.description}}</p>
                          {{/if}}
                        </div>

                        {{#if room.capacityLabel}}
                          <span class="discourse-events-calendar__resource-meta">
                            Capacity {{room.capacityLabel}}
                          </span>
                        {{/if}}
                      </li>
                    {{/each}}
                  </ol>
                {{else}}
                  <p class="discourse-events-calendar__empty">
                    No rooms are visible at this venue.
                  </p>
                {{/if}}
              </section>
            </aside>
          </div>
        {{/if}}
      </section>
    {{else}}
      <section class="discourse-events-calendar__panel">
        <h1>Venue unavailable</h1>

        {{#if @controller.loadError}}
          <div class="discourse-events-calendar__error" role="alert">
            {{@controller.loadError}}
          </div>
        {{else}}
          <p class="discourse-events-calendar__empty">
            This venue could not be found.
          </p>
        {{/if}}
      </section>
    {{/if}}
  </div>
</template>
